import * as React from "react"
import { useMemo } from "react"
import { Box, Typography, Skeleton } from "@mui/material"
import { TableSeeker, IconSeeker } from "@ui-seeker/core"

import { tableData, totalCount, isLoadingTable, selectedRows } from "../@signals"
import {
    searchTextColumn,
    productIdColumn,
    idMappingColumn,
    eanColumn,
    rawTitleColumn,
    rawSubtitleColumn,
    rawVariantColumn,
    siteColumn,
    brandColumn,
    batchColumn,
    rawProductColumn,
    iaInfoColumn,
    statusColumn,
    actionsColumn
} from "./columns"
import SkuCandidatesTableToolbar from "./CustomToolbar"


export default function SkuCandidatesTable() {
    const data = tableData.value
    const total = totalCount.value
    const isLoading = isLoadingTable.value

    // Transform data for TableSeeker - flatten the structure
    const tableRows = useMemo(() => {
        return data.map(item => {
            // Build combined search text with all searchable fields
            const searchParts = [
                item.raw?.product_id,
                item.raw?.ean,
                item.raw?.title,
                item.raw?.subtitle,
                item.raw?.variant_raw,
                item.raw?.brand,
                item.raw?.site,
                item.raw?.reference
            ].filter(Boolean)

            return {
                search_text: searchParts.join(' '),
                product_id: item.raw?.product_id || '',
                id_mapping: item.id_mapping,
                ean: item.raw?.ean || '',
                raw_title: item.raw?.title || '',
                raw_subtitle: item.raw?.subtitle || '',
                raw_variant: item.raw?.variant_raw || '',
                site: item.raw?.site || '-',
                brand: item.raw?.brand || '-',
                batch_info: {
                    id_batch: item.id_batch,
                    ts_finished: item.ts_finished
                },
                raw: item.raw,
                ia_info: {
                    ia_text: item.ia_text,
                    ia_options: item.ia_options
                },
                status: 'not_found',
                actions_data: {
                    id_mapping: item.id_mapping,
                    raw: item.raw
                }
            }
        })
    }, [data])

    const handleSelectionChange = (currentRowsSelected, allRowsSelected, rowsSelected) => {
        const selectedData = rowsSelected.map(index => {
            const row = tableRows[index]
            return {
                id_mapping: row.id_mapping,
                id_site: row.raw?.id_site,
                product_id: row.raw?.product_id,
                raw: row.raw
            }
        })
        selectedRows.value = selectedData
    }

    if (isLoading) {
        return <Skeleton variant="rectangular" height={500} sx={{ mt: 2 }} />
    }

    if (data.length === 0 && total === 0) {
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 8,
                color: '#9ca3af',
                backgroundColor: '#fff',
                borderRadius: 1,
                border: '1px solid #e5e7eb'
            }}>
                <IconSeeker icon="magnifying-glass" sx={{ fontSize: 48, mb: 2 }} />
                <Typography variant="body1">
                    Select filters and click Search to view SKU candidates
                </Typography>
            </Box>
        )
    }

    return (
        <>
            <TableSeeker
                data={tableRows}
                title={`SKU Candidates (${total} products)`}
                columns={[
                    searchTextColumn(),
                    productIdColumn(),
                    idMappingColumn(),
                    eanColumn(),
                    rawTitleColumn(),
                    rawSubtitleColumn(),
                    rawVariantColumn(),
                    siteColumn(),
                    brandColumn(),
                    batchColumn(),
                    rawProductColumn(),
                    iaInfoColumn(),
                    statusColumn(),
                    actionsColumn()
                ]}
                options={{
                    selectableRows: 'multiple',
                    onRowSelectionChange: handleSelectionChange,
                    customToolbarSelect: (selectedData, setSelectedRows) => (
                        <SkuCandidatesTableToolbar
                            selectedData={selectedData}
                            setSelectedRows={setSelectedRows}
                        />
                    ),
                    sortOrder: {
                        name: 'batch_info',
                        direction: 'desc'
                    },
                    rowsPerPage: 100,
                    rowsPerPageOptions: [10, 20, 50, 100, 200]
                }}
                textLabels={{
                    body: {
                        noMatch: 'No SKU candidates found with the selected filters.'
                    }
                }}
            />
        </>
    )
}
